import type { MonthlyDemandRow } from "../types";

export type LabCompany = "openai" | "anthropic";
export type TokenSurface = "chat" | "agent";
export type TokenTask = "office" | "coding" | "document" | "research" | "customer-support" | "other";

export interface PlatformMonthlyEvidence {
  month: string;
  openRouterTotalTokensT: number;
  openRouterOpenAiTokensT: number;
  openRouterAnthropicTokensT: number;
  codexDownloads: number;
  claudeCodeDownloads: number;
}

export interface TokenTaskAssumption {
  task: TokenTask;
  surface: TokenSurface;
  eligibleUsersM: number;
  penetration: number;
  activeRate: number;
  activeDays: number;
  tasksPerDay: number;
  tokensPerStep: number;
  agentSteps: number;
}

export interface ArrAnchor {
  company: LabCompany;
  observedAt: string;
  availableAt: string;
  arrB: number;
  kind: "official-retrospective" | "reported-retrospective" | "company-reported-run-rate"
    | "media-reported-run-rate" | "official-lower-bound" | "official-run-rate";
  backtestEligible: boolean;
  sourceUrl: string;
}

export interface ArrSegmentAssumption {
  anchorArrB: number;
  codingB: number;
  b2bApiB: number;
  generalSubscriptionsB: number;
}

export interface ArrExternalBenchmark {
  id: string;
  provider: string;
  scope: LabCompany | "combined";
  metric: "annualized-revenue-run-rate";
  valueB: number;
  asOf: string;
  publishedAt: string;
  estimateType: "analyst-estimate";
  evidenceLevel: "primary-public" | "secondary-attribution" | "paywalled-origin" | "user-supplied-paywalled-reference";
  exactness: "exact" | "approximate" | "lower-bound" | "point-estimate";
  accountingBasis: "gross" | "net" | "mixed" | "unknown";
  sourceUrl: string;
  comparabilityNote: string;
  benchmarkEligible: boolean;
  /** May update the current model level, but can never enter historical fitting or OOS tests. */
  calibrationEligible: boolean;
  backtestEligible: false;
}

export interface TokenArrEvidence {
  schemaVersion: 2;
  asOf: string;
  platformMonthly: PlatformMonthlyEvidence[];
  tokenTasks: TokenTaskAssumption[];
  arrAnchors: ArrAnchor[];
  arrSegments: Record<LabCompany, ArrSegmentAssumption>;
  arrExternalBenchmarks: ArrExternalBenchmark[];
  sources: Array<{ id: string; tier: string; scope: string; cadence: string; url: string }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isoDate(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error(`${label} must be YYYY-MM-DD.`);
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) throw new Error(`${label} is not a calendar date.`);
}

function positive(value: unknown, label: string, allowZero = false): asserts value is number {
  if (!Number.isFinite(value) || (allowZero ? Number(value) < 0 : Number(value) <= 0)) {
    throw new Error(`${label} must be ${allowZero ? "non-negative" : "positive"}.`);
  }
}

export function parseTokenArrEvidence(value: unknown): TokenArrEvidence {
  if (!isRecord(value) || value.schemaVersion !== 2) throw new Error("Token/ARR evidence schema mismatch.");
  isoDate(value.asOf, "Token/ARR evidence asOf");
  if (!Array.isArray(value.platformMonthly) || value.platformMonthly.length < 12) throw new Error("Token platform history needs at least 12 months.");
  if (!Array.isArray(value.tokenTasks) || value.tokenTasks.length < 6) throw new Error("Token task assumptions are incomplete.");
  if (!Array.isArray(value.arrAnchors) || value.arrAnchors.length < 8) throw new Error("ARR milestone history is incomplete.");
  if (!isRecord(value.arrSegments) || !Array.isArray(value.arrExternalBenchmarks) || !Array.isArray(value.sources)) {
    throw new Error("Token/ARR evidence metadata is incomplete.");
  }

  const evidence = value as unknown as TokenArrEvidence;
  const months = new Set<string>();
  for (const row of evidence.platformMonthly) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(row.month) || months.has(row.month)) throw new Error(`Invalid or duplicate Token month: ${row.month}`);
    months.add(row.month);
    for (const field of ["openRouterTotalTokensT", "openRouterOpenAiTokensT", "openRouterAnthropicTokensT", "codexDownloads", "claudeCodeDownloads"] as const) {
      positive(row[field], `${row.month} ${field}`, true);
    }
    if (row.openRouterOpenAiTokensT + row.openRouterAnthropicTokensT > row.openRouterTotalTokensT + 1e-9) {
      throw new Error(`${row.month} provider Token subtotal exceeds OpenRouter total.`);
    }
  }
  const sortedMonths = [...months].sort();
  if (evidence.platformMonthly.some((row, index) => row.month !== sortedMonths[index])) throw new Error("Token platform months must be sorted.");

  const taskKeys = new Set<string>();
  for (const row of evidence.tokenTasks) {
    const key = `${row.task}:${row.surface}`;
    if (taskKeys.has(key)) throw new Error(`Duplicate Token task assumption: ${key}`);
    taskKeys.add(key);
    for (const field of ["eligibleUsersM", "activeDays", "tasksPerDay", "tokensPerStep", "agentSteps"] as const) positive(row[field], `${key} ${field}`);
    for (const field of ["penetration", "activeRate"] as const) {
      positive(row[field], `${key} ${field}`);
      if (row[field] > 1) throw new Error(`${key} ${field} must be <= 1.`);
    }
    if (row.surface === "chat" && row.agentSteps !== 1) throw new Error(`${key} chat must have exactly one step.`);
  }
  if (![...taskKeys].some((key) => key.endsWith(":chat")) || ![...taskKeys].some((key) => key.endsWith(":agent"))) {
    throw new Error("Token task assumptions require both chat and agent surfaces.");
  }

  const anchorKeys = new Set<string>();
  for (const row of evidence.arrAnchors) {
    if (row.company !== "openai" && row.company !== "anthropic") throw new Error(`Unknown ARR company: ${row.company}`);
    isoDate(row.observedAt, `${row.company} observedAt`);
    isoDate(row.availableAt, `${row.company} availableAt`);
    positive(row.arrB, `${row.company} ARR`);
    if (!/^https:\/\//.test(row.sourceUrl)) throw new Error(`${row.company} ARR anchor lacks an HTTPS source.`);
    const key = `${row.company}:${row.observedAt}:${row.availableAt}`;
    if (anchorKeys.has(key)) throw new Error(`Duplicate ARR anchor: ${key}`);
    anchorKeys.add(key);
  }
  for (const company of ["openai", "anthropic"] as const) {
    const segment = evidence.arrSegments[company];
    if (!segment) throw new Error(`${company} ARR segment assumptions are missing.`);
    for (const field of ["anchorArrB", "codingB", "b2bApiB", "generalSubscriptionsB"] as const) positive(segment[field], `${company} ${field}`, true);
    const sum = segment.codingB + segment.b2bApiB + segment.generalSubscriptionsB;
    if (Math.abs(sum - segment.anchorArrB) > 1e-8) throw new Error(`${company} ARR segments do not reconcile to the anchor.`);
  }
  const benchmarkIds = new Set<string>();
  for (const row of evidence.arrExternalBenchmarks) {
    if (!row.id || benchmarkIds.has(row.id)) throw new Error(`Invalid or duplicate ARR external benchmark id: ${row.id}`);
    benchmarkIds.add(row.id);
    if (row.scope !== "openai" && row.scope !== "anthropic" && row.scope !== "combined") {
      throw new Error(`Unknown ARR external benchmark scope: ${row.scope}`);
    }
    if (row.metric !== "annualized-revenue-run-rate" || row.estimateType !== "analyst-estimate") {
      throw new Error(`${row.id} has an unsupported ARR benchmark definition.`);
    }
    isoDate(row.asOf, `${row.id} asOf`);
    isoDate(row.publishedAt, `${row.id} publishedAt`);
    positive(row.valueB, `${row.id} valueB`);
    if (!/^https:\/\//.test(row.sourceUrl)) throw new Error(`${row.id} lacks an HTTPS source.`);
    if (row.backtestEligible !== false) throw new Error(`${row.id} external benchmark must never enter ARR backtests.`);
    if (typeof row.calibrationEligible !== "boolean") throw new Error(`${row.id} needs an explicit calibration eligibility flag.`);
    if (row.calibrationEligible && (row.scope === "combined" || row.exactness !== "point-estimate")) {
      throw new Error(`${row.id} calibration must be a company-level point estimate.`);
    }
    if (typeof row.comparabilityNote !== "string" || !row.comparabilityNote.trim()) {
      throw new Error(`${row.id} needs a comparability note.`);
    }
  }
  return evidence;
}

export function mergePlatformEvidence(
  monthly: readonly MonthlyDemandRow[],
  evidence: readonly PlatformMonthlyEvidence[],
  asOf: string,
): PlatformMonthlyEvidence[] {
  isoDate(asOf, "Platform evidence cutoff");
  const currentMonth = asOf.slice(0, 7);
  const byMonth = new Map(evidence.filter((row) => row.month < currentMonth).map((row) => [row.month, { ...row }]));
  for (const row of monthly) {
    if (row.month >= currentMonth) continue;
    const prior = byMonth.get(row.month);
    if (!prior) {
      if ([row.openRouterTokensT, row.openRouterOpenAiTokensT, row.openRouterAnthropicTokensT, row.codexDownloads, row.claudeCodeDownloads]
        .every((value) => Number.isFinite(value))) {
        byMonth.set(row.month, {
          month: row.month,
          openRouterTotalTokensT: row.openRouterTokensT as number,
          openRouterOpenAiTokensT: row.openRouterOpenAiTokensT as number,
          openRouterAnthropicTokensT: row.openRouterAnthropicTokensT as number,
          codexDownloads: row.codexDownloads as number,
          claudeCodeDownloads: row.claudeCodeDownloads as number,
        });
      }
      continue;
    }
    const next = {
      ...prior,
      openRouterTotalTokensT: row.openRouterTokensT ?? prior.openRouterTotalTokensT,
      openRouterOpenAiTokensT: row.openRouterOpenAiTokensT ?? prior.openRouterOpenAiTokensT,
      openRouterAnthropicTokensT: row.openRouterAnthropicTokensT ?? prior.openRouterAnthropicTokensT,
      codexDownloads: row.codexDownloads ?? prior.codexDownloads,
      claudeCodeDownloads: row.claudeCodeDownloads ?? prior.claudeCodeDownloads,
    };
    byMonth.set(row.month, next);
  }
  return [...byMonth.values()].sort((left, right) => left.month.localeCompare(right.month));
}
